import { archivedTwinLogicQueryAuthoritySchema } from "./twin-logic-execution-authority.js";
import { z } from "zod";
import { pluginConnectionHealthSchema } from "./plugin-connection-health.js";
import { archivedTwinQuerySemanticsSchema } from "./twin-query-semantics.js";
import { sessionRoutingArchitectureSchema, taskRunCapabilitySchema, } from "./primitives.js";
import { twinLogicQueryOperationSchema, } from "./twin-logic.js";
export const mlxWorkspaceOpenDataSetViewToolName = "twin_workspace_open_data_set_view";
export const twinGovernedProductQueryToolName = "twin_governed_product_query";
export * from "./twin-governed-query.js";
export const mlxWorkspaceOpenDataSetViewInputSchema = {
    type: "object",
    additionalProperties: false,
    properties: {
        channelId: {
            type: "string",
            minLength: 1,
            description: "Exact channel id returned by twin_workspace_list_channels.",
        },
        objectTypeId: {
            type: "string",
            minLength: 1,
            description: "Exact Data set id returned in twin_workspace_list_channel_schema.objectTypes.",
        },
        viewId: {
            type: "string",
            minLength: 1,
            description: "Exact saved View id returned in twin_workspace_list_channel_schema.views.",
        },
    },
    required: ["channelId"],
};
export const runtimePluginToolSurfaceSchema = z.enum([
    "electric-tool",
    "mcp",
    "cli",
    "http",
]);
export const runtimePluginCompositionSchema = z.object({
    dependsOnPluginSlugs: z.array(z.string().trim().min(1)).default([]),
});
export const runtimePluginRoutingClassSchema = z.enum([
    "governed-data",
    "raw-source-adapter",
    "application",
]);
export const runtimePluginToolSchema = z.object({
    name: z.string().trim().min(1),
    adapterSlug: z.string().trim().min(1).optional(),
    label: z.string().trim().min(1),
    description: z.string().trim().min(1),
    runtimeSurface: runtimePluginToolSurfaceSchema,
    inputSchema: z.record(z.string(), z.unknown()).optional(),
    access: z.object({
        toolGroupId: z.string().trim().min(1).regex(/^[a-z0-9][a-z0-9_-]*$/u),
        actionId: z.enum(["plugin.tool.execute.read", "plugin.tool.execute.write", "plugin.tool.execute.egress"]),
        sideEffect: z.enum(["read", "write", "destructive"]),
        egress: z.enum(["none", "internal-share", "external-send"]),
        approval: z.enum(["never", "always", "policy"]),
        reviewedDefinitionHash: z.string().regex(/^[a-f0-9]{64}$/u),
        mcpPins: z.array(z.object({
            serverId: z.string().trim().min(1),
            toolName: z.string().trim().min(1),
            definitionHash: z.string().regex(/^[a-f0-9]{64}$/u),
        }).strict()).optional(),
    }).strict().optional(),
});
export const runtimePluginSkillReferenceSchema = z.object({
    pluginSlug: z.string().trim().min(1),
    skillSlug: z.string().trim().min(1),
});
export const runtimePluginBrandingSchema = z.object({
    icon: z.string().trim().min(1).optional(),
    iconUrl: z.string().trim().min(1).optional(),
    color: z.string().trim().min(1).optional(),
    websiteUrl: z.string().url().optional(),
    docsUrl: z.string().url().optional(),
});
export const runtimePluginManifestBrandingSchema = runtimePluginBrandingSchema.extend({
    iconPath: z.string().trim().min(1).optional(),
});
export const runtimePluginInstallStepSchema = z.object({
    label: z.string().trim().min(1),
    description: z.string().trim().min(1),
    href: z.string().url().optional(),
});
export const runtimePluginInstallSchema = z.object({
    summary: z.string().trim().min(1),
    authType: z
        .enum(["api-key", "oauth", "service-account", "cli-auth"])
        .optional(),
    instructions: z.array(runtimePluginInstallStepSchema).default([]),
});
export const runtimePluginSkillSchema = z.object({
    slug: z.string().trim().min(1),
    adapterSlug: z.string().trim().min(1).optional(),
    label: z.string().trim().min(1),
    description: z.string().trim().min(1),
    version: z.number().int().positive().optional(),
    contentHash: z.string().regex(/^sha256:[a-f0-9]{64}$/u).optional(),
    routingDescription: z.string().trim().max(600).optional(),
    activationPhrases: z.array(z.string().trim().min(1)).default([]),
    defaultTools: z.array(z.string().trim().min(1)),
    samplePrompts: z.array(z.string().trim().min(1)).default([]),
    requiresSkills: z.array(runtimePluginSkillReferenceSchema).optional(),
    instance: z.object({
        kind: z.literal("postgres-connection"),
        id: z.string().trim().min(1).max(100),
        skillId: z.string().trim().min(1).max(120),
        skillVersionId: z.string().trim().min(1).max(120),
        version: z.number().int().positive(),
    }).strict().optional(),
    inlineMarkdown: z.string().min(1).max(100_000).optional(),
});
export const runtimePluginManifestSkillSchema = runtimePluginSkillSchema
    .omit({ instance: true, inlineMarkdown: true })
    .extend({
    markdownPath: z.string().trim().min(1),
});
export const runtimePluginManifestSecretSchema = z.object({
    name: z.string().trim().min(1),
    provider: z.enum(["infisical"]),
    scope: z.enum(["tenant", "organisation", "user"]).default("tenant"),
});
export const runtimePluginSetupSchema = z.object({
    auth: z.enum([
        "tenant-warehouse-scope",
        "infisical-plugin-secret",
        "provider-managed-oauth",
        "native-user-oauth",
        "native-cli-login",
        "organisation-data-source",
    ]),
    adapters: z.array(z.string().trim().min(1)),
    requiredEnv: z.array(z.string().trim().min(1)),
    optionalEnv: z.array(z.string().trim().min(1)).default([]),
    secretRefs: z.array(z.string().trim().min(1)).default([]),
});
export const runtimePluginPolicySchema = z.object({
    requiredCapabilities: z.array(taskRunCapabilitySchema),
    hitl: z.enum(["none", "writes"]),
    allowlist: z.enum([
        "tenant-warehouse-scope",
        "tenant-plugin-secret",
        "organisation-data-source",
    ]),
    tenantSlugs: z.array(z.string().trim().min(1)).optional(),
});
export const runtimePluginMcpServerSchema = z.object({
    type: z.enum(["http"]),
    url: z.string().url(),
    oauth: z
        .object({
        clientId: z.string().trim().min(1).optional(),
        callbackPort: z.number().int().positive().optional(),
    })
        .optional(),
});
export const runtimePluginMcpSchema = z.object({
    name: z.string().trim().min(1).optional(),
    mcpServers: z.record(z.string(), runtimePluginMcpServerSchema).optional(),
    source: z.string().url().optional(),
    status: z
        .enum(["reference-only", "native-tool-bridge", "adapter-pending"])
        .default("reference-only"),
});
export const runtimePluginIntegrationSchema = z.discriminatedUnion("provider", [
    z.object({
        provider: z.enum(["native", "warehouse", "connector", "mcp"]),
        connectorProviders: z.array(z.string().trim().min(1).regex(/^[a-z0-9][a-z0-9_.-]*$/u)).optional(),
    }),
    z.object({
        provider: z.literal("composio"),
        toolkitSlug: z.string().trim().min(1),
        authConfigId: z.string().trim().min(1).optional(),
        authConfigRequired: z.boolean().optional(),
        mode: z.enum(["tool-router"]).default("tool-router"),
        connectorProviders: z.array(z.string().trim().min(1).regex(/^[a-z0-9][a-z0-9_.-]*$/u)).optional(),
    }),
]);
export const runtimePluginSecretDeclarationSchema = z.object({
    name: z.string().trim().min(1),
    provider: z.enum(["infisical"]),
    scope: z.enum(["tenant", "organisation", "user"]),
});
export const runtimePluginSecretStatusSchema = z.object({
    pluginSlug: z.string().trim().min(1),
    secretName: z.string().trim().min(1),
    provider: z.enum(["infisical"]),
    scope: z.enum(["tenant", "organisation", "user"]),
    organisationId: z.string().trim().min(1).optional(),
    scopeSubjectId: z.string().trim().min(1).optional(),
    configured: z.boolean(),
    secretPath: z.string().trim().min(1).optional(),
    last4: z.string().trim().min(1).optional(),
    updatedAt: z.number().optional(),
});
export const listRuntimePluginSecretStatusResponseSchema = z.object({
    pluginSlug: z.string().trim().min(1),
    secrets: z.array(runtimePluginSecretStatusSchema),
});
export const runtimePluginSetupRequirementSchema = z.enum([
    "plugin-secret",
    "tenant-warehouse-scope",
    "provider-managed-oauth",
    "native-user-oauth",
    "native-cli-login",
    "organisation-data-source",
]);
export const runtimePluginSetupStatusSchema = z.object({
    pluginSlug: z.string().trim().min(1),
    requirement: runtimePluginSetupRequirementSchema,
    configured: z.boolean(),
    detail: z.string().trim().min(1).optional(),
    organisationIds: z.array(z.string().trim().min(1)).optional(),
    provider: z.enum(["composio"]).optional(),
    toolkitSlug: z.string().trim().min(1).optional(),
    connectionStatus: z
        .enum(["not-configured", "pending", "connected", "disabled", "error"])
        .optional(),
    externalConnectionId: z.string().trim().min(1).optional(),
    accountLabel: z.string().trim().min(1).optional(),
    accountEmail: z.string().trim().min(1).optional(),
    workspaceId: z.string().trim().min(1).optional(),
    workspaceName: z.string().trim().min(1).optional(),
    cliVersion: z.string().trim().min(1).optional(),
    providerAuthRequired: z.boolean().optional(),
    providerAuthConfigured: z.boolean().optional(),
    authConfigId: z.string().trim().min(1).optional(),
    authConfigSource: z
        .enum(["composio-managed", "custom-oauth", "manual"])
        .optional(),
    configuredConnectionCount: z.number().int().nonnegative().optional(),
    healthyConnectionCount: z.number().int().nonnegative().optional(),
});
export const listRuntimePluginSetupStatusResponseSchema = z.object({
    plugins: z.array(runtimePluginSetupStatusSchema),
});
export const upsertRuntimePluginSecretRequestSchema = z.object({
    value: z.string().trim().min(1).max(20_000),
});
export const upsertRuntimePluginSecretResponseSchema = runtimePluginSecretStatusSchema;
export const deleteRuntimePluginSecretResponseSchema = z.object({
    ok: z.boolean(),
    pluginSlug: z.string().trim().min(1),
    secretName: z.string().trim().min(1),
});
export const disconnectRuntimePluginResponseSchema = z.object({
    ok: z.boolean(),
    pluginSlug: z.string().trim().min(1),
    requirement: runtimePluginSetupRequirementSchema,
    removedLocalSecrets: z.number().int().nonnegative(),
    removedSecretStatuses: z.number().int().nonnegative(),
    removedUserConnections: z.number().int().nonnegative(),
    revokedExternalConnections: z.number().int().nonnegative(),
    deletedExternalConnections: z.number().int().nonnegative(),
});
export const runtimePluginConnectionStatusSchema = z.enum([
    "pending",
    "connected",
    "disabled",
    "error",
]);
export const runtimePluginConnectionSchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    userContextId: z.string().trim().min(1),
    pluginSlug: z.string().trim().min(1),
    provider: z.enum(["composio"]),
    toolkitSlug: z.string().trim().min(1),
    status: runtimePluginConnectionStatusSchema,
    externalConnectionId: z.string().trim().min(1).optional(),
    externalSessionId: z.string().trim().min(1).optional(),
    authConfigId: z.string().trim().min(1).optional(),
    accountLabel: z.string().trim().min(1).optional(),
    accountEmail: z.string().trim().min(1).optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    createdAt: z.number(),
    updatedAt: z.number(),
    lastVerifiedAt: z.number().optional(),
});
export const listRuntimePluginConnectionsResponseSchema = z.object({
    connections: z.array(runtimePluginConnectionSchema),
});
export const upsertRuntimePluginConnectionRequestSchema = z.object({
    pluginSlug: z.string().trim().min(1),
    provider: z.enum(["composio"]),
    toolkitSlug: z.string().trim().min(1),
    status: runtimePluginConnectionStatusSchema,
    externalConnectionId: z.string().trim().min(1).optional(),
    externalSessionId: z.string().trim().min(1).optional(),
    authConfigId: z.string().trim().min(1).optional(),
    accountLabel: z.string().trim().min(1).optional(),
    accountEmail: z.string().trim().min(1).optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
});
export const syncRuntimePluginProviderCatalogResponseSchema = z.object({
    provider: z.enum(["composio"]),
    synced: z.number().int().nonnegative(),
    totalItems: z.number().int().nonnegative(),
    pages: z.number().int().positive(),
    disabled: z.number().int().nonnegative(),
    complete: z.literal(true),
}).refine(({ synced, totalItems }) => synced === totalItems, {
    path: ["synced"],
    message: "A complete provider catalog must sync every reported item.",
});
export const runtimePluginTelemetryWindowHoursSchema = z.union([
    z.literal(1),
    z.literal(24),
    z.literal(168),
    z.literal(720),
]);
export const runtimePluginTelemetryEntrySchema = z.object({
    pluginSlug: z.string().trim().min(1),
    invocations: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    medianDurationMs: z.number().nonnegative().nullable(),
    p95DurationMs: z.number().nonnegative().nullable(),
    lastInvokedAt: z.number().nonnegative(),
    runtimeSurfaces: z.array(z.string().trim().min(1)).max(8),
    adapterSlugs: z.array(z.string().trim().min(1)).max(8),
}).strict().refine(({ invocations, succeeded, failed }) => invocations === succeeded + failed, {
    path: ["invocations"],
    message: "Plugin runtime invocation totals must equal succeeded plus failed.",
});
export const runtimePluginTelemetryResponseSchema = z.object({
    plane: z.literal("runtime"),
    source: z.literal("task_run_events"),
    ingestionIncluded: z.literal(false),
    window: z.object({
        hours: runtimePluginTelemetryWindowHoursSchema,
        startedAt: z.number().nonnegative(),
        endedAt: z.number().nonnegative(),
    }).strict().refine(({ startedAt, endedAt }) => startedAt <= endedAt, {
        path: ["startedAt"],
        message: "Telemetry window must start before it ends.",
    }),
    totals: z.object({
        plugins: z.number().int().nonnegative(),
        invocations: z.number().int().nonnegative(),
        succeeded: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    }).strict(),
    returnedPlugins: z.number().int().nonnegative().max(100),
    truncated: z.boolean(),
    plugins: z.array(runtimePluginTelemetryEntrySchema).max(100),
}).strict().superRefine((telemetry, context) => {
    if (telemetry.returnedPlugins !== telemetry.plugins.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["returnedPlugins"],
            message: "returnedPlugins must match the plugin row count.",
        });
    }
    if (telemetry.totals.invocations !==
        telemetry.totals.succeeded + telemetry.totals.failed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["totals", "invocations"],
            message: "Runtime totals must equal succeeded plus failed.",
        });
    }
    if (telemetry.truncated !==
        (telemetry.returnedPlugins < telemetry.totals.plugins)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["truncated"],
            message: "truncated must reflect omitted plugin rows.",
        });
    }
});
export const runtimePluginProviderAuthConfigSourceSchema = z.enum([
    "composio-managed",
    "custom-oauth",
    "manual",
]);
export const runtimePluginProviderAuthConfigSchema = z.object({
    provider: z.enum(["composio"]),
    toolkitSlug: z.string().trim().min(1),
    requiresAuthConfig: z.boolean(),
    configured: z.boolean(),
    authConfigId: z.string().trim().min(1).optional(),
    authScheme: z.string().trim().min(1).optional(),
    source: runtimePluginProviderAuthConfigSourceSchema.optional(),
});
export const pluginTaskAdapterSchema = z.enum([
    "composio",
    "native",
    "disabled",
]);
export const pluginSyncAdapterSchema = z.enum(["native", "disabled"]);
export const pluginFallbackTaskAdapterSchema = z.enum(["composio", "native"]);
export const pluginProviderAdapterPolicySchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1).optional(),
    providerSlug: z.string().trim().min(1),
    taskAdapter: pluginTaskAdapterSchema,
    syncAdapter: pluginSyncAdapterSchema,
    fallbackTaskAdapter: pluginFallbackTaskAdapterSchema.optional(),
    enabled: z.boolean(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const listPluginProviderAdapterPoliciesResponseSchema = z.object({
    policies: z.array(pluginProviderAdapterPolicySchema),
});
export const upsertPluginProviderAdapterPolicyRequestSchema = z.object({
    organisationId: z.string().trim().min(1).optional(),
    providerSlug: z.string().trim().min(1),
    taskAdapter: pluginTaskAdapterSchema,
    syncAdapter: pluginSyncAdapterSchema,
    fallbackTaskAdapter: pluginFallbackTaskAdapterSchema.optional(),
    enabled: z.boolean().default(true),
    metadata: z.record(z.string(), z.unknown()).default({}),
});
export const runtimePluginSkillOverlayMatchModeSchema = z.enum([
    "always",
    "prompt",
    "explicit",
]);
const governedProductQueryFieldSchema = z.string().regex(/^[a-z][a-z0-9_]*$/u);
const governedProductFieldAggregationSchema = z.enum([
    "none",
    "sum",
    "count",
    "count_distinct",
    "average",
    "minimum",
    "maximum",
]);
const governedProductFieldAdditivitySchema = z.enum([
    "additive",
    "semi_additive",
    "non_additive",
    "not_applicable",
]);
const governedProductFieldRollupSchema = z.enum([
    "count_non_null",
    "count_distinct",
    "sum",
    "average",
    "minimum",
    "maximum",
]);
export const governedProductMeasurePolicySchema = z.object({
    field: governedProductQueryFieldSchema,
    sourceAggregation: governedProductFieldAggregationSchema,
    additivity: governedProductFieldAdditivitySchema,
    allowedAggregations: z.array(governedProductFieldRollupSchema).max(6)
        .refine((values) => new Set(values).size === values.length, "Allowed measure aggregations must be unique."),
}).strict().superRefine((policy, context) => {
    if (policy.sourceAggregation === "count_distinct" && policy.additivity !== "non_additive") {
        context.addIssue({
            code: "custom",
            path: ["additivity"],
            message: "A count-distinct output must be non-additive.",
        });
    }
    if (policy.additivity !== "additive" &&
        policy.allowedAggregations.some((operation) => operation === "sum" || operation === "average")) {
        context.addIssue({
            code: "custom",
            path: ["allowedAggregations"],
            message: "Only additive measures may allow sum or average roll-ups.",
        });
    }
});
export const governedProductQueryPolicySchema = z.object({
    approvedDimensions: z.array(governedProductQueryFieldSchema).max(80),
    approvedMeasures: z.array(governedProductQueryFieldSchema).max(80),
    measurePolicies: z.array(governedProductMeasurePolicySchema).max(80).optional(),
    allowedOperations: z.array(twinLogicQueryOperationSchema).max(8).optional(),
    allowedFilters: z.array(governedProductQueryFieldSchema).max(256).optional(),
    blockedOperations: z.array(twinLogicQueryOperationSchema).max(8).optional(),
}).strict().superRefine((policy, context) => {
    if (!policy.measurePolicies)
        return;
    const measureFields = policy.measurePolicies.map(({ field }) => field);
    if (new Set(measureFields).size !== measureFields.length) {
        context.addIssue({
            code: "custom",
            path: ["measurePolicies"],
            message: "Measure policies must have unique fields.",
        });
    }
    const approvedMeasures = [...new Set(policy.approvedMeasures)].sort();
    const governedMeasures = [...new Set(measureFields)].sort();
    if (JSON.stringify(approvedMeasures) !== JSON.stringify(governedMeasures)) {
        context.addIssue({
            code: "custom",
            path: ["measurePolicies"],
            message: "Measure policies must cover exactly the approved measures.",
        });
    }
});
export const runtimePluginSkillOverlaySchema = z.object({
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1).optional(),
    pluginSlug: z.string().trim().min(1),
    baseSkillSlug: z.string().trim().min(1),
    overlaySkillSlug: z.string().trim().min(1),
    skillId: z.string().trim().min(1),
    skillVersionId: z.string().trim().min(1),
    version: z.number().int().positive(),
    name: z.string().trim().min(1),
    description: z.string().trim(),
    routingDescription: z.string().trim().max(600).optional(),
    markdown: z.string().min(1),
    triggerPhrases: z.array(z.string().trim().min(1)).default([]),
    matchMode: runtimePluginSkillOverlayMatchModeSchema,
    priority: z.number().int(),
    /*
     * Whether the semantic router highlighted this overlay's base skill (or its
     * match mode makes it mandatory). Prompt-mode overlays ship either way and
     * the task model picks from the full attached catalog. Defaults true for
     * serialized compatibility.
     */
    promptMatched: z.boolean().default(true),
    governedProductBinding: z.object({
        productSlug: z.string().trim().min(1),
        productVersion: z.number().int().positive(),
        publicationVersionId: z.string().trim().min(1),
        schema: z.enum(["mlx_curated", "connector_warehouse"]),
        relation: z.string().regex(/^[a-z][a-z0-9_]*$/u),
        queryPolicy: governedProductQueryPolicySchema.optional(),
        querySemantics: archivedTwinQuerySemanticsSchema.optional(),
        twinLogicAuthority: archivedTwinLogicQueryAuthoritySchema.optional(),
        /*
         * The relations the product is built from ("connector_warehouse.github_commits"), which carry
         * their system's name: a canvas's sign-off names those systems. Accepted before the tenant sends
         * it, so executors on the release before never meet an unknown key.
         */
        sourceRelations: z.array(z.string().regex(/^(?:connector_warehouse|mlx_curated)\.[a-z_][a-z0-9_]{0,126}$/u)).max(128).optional(),
    }).strict().optional(),
    candidateLegacyRelation: z.object({
        generationClass: z.literal("legacy_wrapped"),
        buildId: z.string().trim().min(1), catalogSha256: z.string().regex(/^[0-9a-f]{64}$/u),
        evidenceCutId: z.string().trim().min(1),
        evidenceCutManifestSha256: z.string().regex(/^[0-9a-f]{64}$/u),
        candidateReleaseSha: z.string().regex(/^[0-9a-f]{7,64}$/u),
        definitionSetId: z.string().trim().min(1),
        definitionSetSha256: z.string().regex(/^[0-9a-f]{64}$/u),
        productSlug: z.string().trim().min(1), productVersion: z.number().int().positive(),
        publicationVersionId: z.string().trim().min(1).optional(),
        selectionKind: z.enum(["production_head", "candidate_staged"]).optional(),
        factSource: z.enum(["production_published_relation",
            "candidate_staged_publication_relation"]).optional(),
        schema: z.enum(["mlx_curated", "connector_warehouse"]),
        relation: z.string().regex(/^[a-z][a-z0-9_]*$/u),
        publishedSchema: z.enum(["mlx_curated", "connector_warehouse"]).optional(),
        publishedRelation: z.string().regex(/^[a-z][a-z0-9_]*$/u).optional(),
    }).strict().optional(),
});
/*
 * Lightweight reference to a routable plugin. Carried alongside the resolved
 * (attached) plugin set so the runtime can tell the model which connected
 * plugins exist even when this turn's routing attached none of them —
 * without shipping full plugin definitions for unrouted plugins.
 */
export const runtimePluginRefSchema = z.object({
    slug: z.string().trim().min(1),
    label: z.string().trim().min(1),
});
export const canonicalPluginAdapterKindSchema = z.enum([
    "native",
    "warehouse",
    "connector",
    "cli",
    "mcp",
    "provider",
    "http",
]);
export const canonicalPluginAdapterSchema = z.object({
    slug: z.string().trim().min(1),
    kind: canonicalPluginAdapterKindSchema,
    runtimeHandler: z.string().trim().min(1),
    provider: z.string().trim().min(1).optional(),
    externalSlug: z.string().trim().min(1).optional(),
    authKind: z.string().trim().min(1),
    definition: z.record(z.string(), z.unknown()).default({}),
    enabled: z.boolean().default(true),
    position: z.number().int().nonnegative().default(0),
}).strict();
export const canonicalPluginCatalogAliasSchema = z.object({
    provider: z.string().trim().min(1).regex(/^[a-z0-9][a-z0-9_-]*$/u),
    externalSlug: z.string().trim().min(1).regex(/^[a-z0-9][a-z0-9_-]*$/u),
}).strict();
export const pluginExecutionSnapshotSchema = z.object({
    pluginSlug: z.string().trim().min(1),
    pluginVersion: z.string().trim().min(1),
    adapterSlug: z.string().trim().min(1),
    runtimeHandler: z.string().trim().min(1),
    tools: z.array(z.string().trim().min(1)),
    skills: z.array(z.object({
        slug: z.string().trim().min(1),
        version: z.number().int().positive().optional(),
        contentHash: z.string().regex(/^sha256:[a-f0-9]{64}$/u).optional(),
    }).strict()),
}).strict();
export const runtimePluginSchema = z.object({
    slug: z.string().trim().min(1),
    label: z.string().trim().min(1),
    version: z.string().trim().min(1),
    source: z.enum(["bundled"]),
    description: z.string().trim().min(1),
    activationPhrases: z.array(z.string().trim().min(1)).default([]),
    branding: runtimePluginBrandingSchema.optional(),
    install: runtimePluginInstallSchema.optional(),
    enabled: z.boolean(),
    tools: z.array(runtimePluginToolSchema),
    skills: z.array(runtimePluginSkillSchema),
    setup: runtimePluginSetupSchema,
    policy: runtimePluginPolicySchema,
    integration: runtimePluginIntegrationSchema.optional(),
    composition: runtimePluginCompositionSchema.optional(),
    routingClass: runtimePluginRoutingClassSchema.optional(),
    secrets: z.array(runtimePluginSecretDeclarationSchema).default([]),
    mcp: z.array(runtimePluginMcpSchema).default([]),
    adapters: z.array(canonicalPluginAdapterSchema).optional(),
});
const PERSONAL_CONNECTION_AUTH = new Set([
    "native-user-oauth",
    "native-cli-login",
    "provider-managed-oauth",
]);
/** A plugin the person connects with their own account (mail, calendar, chat, files). */
export function isPersonalConnectionPlugin(plugin) {
    return PERSONAL_CONNECTION_AUTH.has(plugin.setup.auth);
}
export function governedProductSlugsFromRuntimePluginToolAuthority(plugin) {
    const tools = plugin.tools.filter((tool) => tool.name === twinGovernedProductQueryToolName);
    if (tools.length === 0)
        return undefined;
    if (tools.length !== 1) {
        throw new Error("router_v1_governed_product_tool_authority_ambiguous");
    }
    const properties = tools[0]?.inputSchema?.properties;
    const productSlug = properties && typeof properties === "object"
        ? properties.productSlug
        : undefined;
    const productSlugs = productSlug && typeof productSlug === "object"
        ? productSlug.enum
        : undefined;
    if (!Array.isArray(productSlugs) ||
        productSlugs.some((slug) => typeof slug !== "string" || slug.trim() === "")) {
        throw new Error("router_v1_governed_product_tool_authority_invalid");
    }
    const canonical = [...new Set(productSlugs)].sort();
    if (canonical.length !== productSlugs.length ||
        canonical.some((slug, index) => slug !== productSlugs[index])) {
        throw new Error("router_v1_governed_product_tool_authority_invalid");
    }
    return canonical;
}
/**
 * Complete catalog authority used by catalog-backed runtimes.
 *
 * This deliberately retains every execution and routing field from the
 * runtime plugin contract.  The older `pluginExecutionSnapshotSchema` is a
 * lossy historical projection and must not be used to authorize a new run.
 */
export const runtimePluginCatalogProjectionSchema = z.array(runtimePluginSchema);
function sortedUnique(values) {
    return [...new Set(values)].sort((left, right) => left.localeCompare(right));
}
function sortedByJson(values) {
    return [...values].sort((left, right) => canonicalJsonValue(left).localeCompare(canonicalJsonValue(right)));
}
/** Return one deterministic, full-fidelity plugin catalog projection. */
export function canonicalRuntimePluginCatalogProjection(plugins) {
    const parsed = runtimePluginCatalogProjectionSchema.parse(plugins);
    return parsed.map((plugin) => ({
        ...plugin,
        activationPhrases: sortedUnique(plugin.activationPhrases),
        tools: sortedByJson(plugin.tools.map((tool) => ({ ...tool }))),
        skills: sortedByJson(plugin.skills.map((skill) => ({
            ...skill,
            activationPhrases: sortedUnique(skill.activationPhrases),
            defaultTools: sortedUnique(skill.defaultTools),
            samplePrompts: sortedUnique(skill.samplePrompts),
            ...(skill.requiresSkills
                ? { requiresSkills: sortedByJson(skill.requiresSkills) }
                : {}),
        }))),
        setup: {
            ...plugin.setup,
            adapters: sortedUnique(plugin.setup.adapters),
            requiredEnv: sortedUnique(plugin.setup.requiredEnv),
            optionalEnv: sortedUnique(plugin.setup.optionalEnv),
            secretRefs: sortedUnique(plugin.setup.secretRefs),
        },
        policy: {
            ...plugin.policy,
            requiredCapabilities: sortedUnique(plugin.policy.requiredCapabilities),
            ...(plugin.policy.tenantSlugs
                ? { tenantSlugs: sortedUnique(plugin.policy.tenantSlugs) }
                : {}),
        },
        ...(plugin.composition
            ? {
                composition: {
                    dependsOnPluginSlugs: sortedUnique(plugin.composition.dependsOnPluginSlugs),
                },
            }
            : {}),
        secrets: sortedByJson(plugin.secrets),
        mcp: sortedByJson(plugin.mcp),
        ...(plugin.adapters ? { adapters: sortedByJson(plugin.adapters) } : {}),
    })).sort((left, right) => left.slug.localeCompare(right.slug));
}
export function canonicalRuntimePluginCatalogJson(plugins) {
    return canonicalJsonValue(canonicalRuntimePluginCatalogProjection(plugins));
}
export const canonicalPluginCapabilitySchema = z.object({
    kind: z.enum(["tool", "skill"]),
    slug: z.string().trim().min(1),
    adapterSlug: z.string().trim().min(1),
    label: z.string().trim().min(1),
    description: z.string().trim().min(1),
    definition: z.record(z.string(), z.unknown()),
    enabled: z.boolean().default(true),
    position: z.number().int().nonnegative().default(0),
}).strict();
export const canonicalPluginCatalogEntrySchema = runtimePluginSchema
    .omit({ source: true, tools: true, skills: true, adapters: true })
    .extend({
    routingClass: runtimePluginRoutingClassSchema,
    catalogAliases: z.array(canonicalPluginCatalogAliasSchema).optional(),
    adapters: z.array(canonicalPluginAdapterSchema).min(1),
    capabilities: z.array(canonicalPluginCapabilitySchema),
})
    .strict()
    .superRefine((plugin, context) => {
    const adapters = new Set(plugin.adapters.map((adapter) => adapter.slug));
    if (adapters.size !== plugin.adapters.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["adapters"],
            message: `Plugin '${plugin.slug}' contains duplicate adapter slugs.`,
        });
    }
    const capabilityKeys = new Set(plugin.capabilities.map((capability) => `${capability.kind}:${capability.slug}`));
    if (capabilityKeys.size !== plugin.capabilities.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["capabilities"],
            message: `Plugin '${plugin.slug}' contains duplicate capability slugs.`,
        });
    }
    for (const capability of plugin.capabilities) {
        if (!adapters.has(capability.adapterSlug)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["capabilities", capability.slug, "adapterSlug"],
                message: `Capability '${capability.slug}' references missing adapter '${capability.adapterSlug}'.`,
            });
        }
    }
});
export const canonicalPluginCatalogDocumentSchema = z.object({
    schemaVersion: z.literal(1),
    plugins: z.array(canonicalPluginCatalogEntrySchema),
}).strict().superRefine((document, context) => {
    const slugs = new Set(document.plugins.map((plugin) => plugin.slug));
    if (slugs.size !== document.plugins.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["plugins"],
            message: "Canonical plugin catalog contains duplicate plugin slugs.",
        });
    }
    const aliasOwners = new Map();
    for (const plugin of document.plugins) {
        for (const alias of plugin.catalogAliases ?? []) {
            const key = `${alias.provider}:${alias.externalSlug}`;
            const owner = aliasOwners.get(key);
            if (owner) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["plugins", plugin.slug, "catalogAliases"],
                    message: `Provider catalog alias '${key}' is claimed by both '${owner}' and '${plugin.slug}'.`,
                });
            }
            else {
                aliasOwners.set(key, plugin.slug);
            }
        }
    }
});
export const canonicalPluginCatalogSnapshotSchema = canonicalPluginCatalogDocumentSchema.extend({
    centralVersion: z.number().int().positive(),
    contentHash: z.string().regex(/^sha256:[a-f0-9]{64}$/u),
}).strict();
export function canonicalPluginCatalogJson(document) {
    return canonicalJsonValue(canonicalPluginCatalogDocumentSchema.parse({
        schemaVersion: document.schemaVersion,
        plugins: document.plugins,
    }));
}
function canonicalJsonValue(value) {
    if (Array.isArray(value)) {
        return `[${value.map(canonicalJsonValue).join(",")}]`;
    }
    if (value && typeof value === "object") {
        return `{${Object.entries(value)
            .filter(([, nested]) => nested !== undefined)
            .sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)
            .map(([key, nested]) => `${JSON.stringify(key)}:${canonicalJsonValue(nested)}`)
            .join(",")}}`;
    }
    return JSON.stringify(value) ?? "null";
}
export const bundledRuntimePluginManifestSchema = runtimePluginSchema
    .omit({ enabled: true, skills: true, branding: true })
    .extend({
    schemaVersion: z.literal(1),
    branding: runtimePluginManifestBrandingSchema.optional(),
    skills: z.array(runtimePluginManifestSkillSchema),
    secrets: z.array(runtimePluginManifestSecretSchema).default([]),
    mcp: z.array(runtimePluginMcpSchema).default([]),
});
export const listRuntimePluginsResponseSchema = z.object({
    plugins: z.array(runtimePluginSchema),
});
export const runtimePluginExecutionBlockReasonSchema = z.enum([
    "not-installed",
    "disabled",
    "missing-connection",
    "missing-auth",
    "adapter-unsupported",
    "router-unsupported",
]);
export const runtimePluginCatalogInstallationSchema = z.object({
    installed: z.boolean(),
    enabled: z.boolean(),
    selectedAdapterSlugs: z.array(z.string().trim().min(1)),
}).strict().superRefine((installation, context) => {
    if (!installation.installed && installation.enabled) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["enabled"],
            message: "A plugin cannot be enabled before it is installed.",
        });
    }
    if (!installation.installed && installation.selectedAdapterSlugs.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["selectedAdapterSlugs"],
            message: "An uninstalled plugin cannot select an adapter.",
        });
    }
});
export const runtimePluginExecutionAvailabilitySchema = z.object({
    routable: z.boolean(),
    router: sessionRoutingArchitectureSchema,
    reasons: z.array(runtimePluginExecutionBlockReasonSchema),
}).strict().superRefine((availability, context) => {
    if (availability.routable && availability.reasons.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["reasons"],
            message: "Routable plugins cannot have execution blockers.",
        });
    }
    if (!availability.routable && availability.reasons.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["reasons"],
            message: "Unroutable plugins must report at least one execution blocker.",
        });
    }
});
export const runtimePluginCatalogEntrySchema = z.object({
    plugin: runtimePluginSchema,
    installation: runtimePluginCatalogInstallationSchema,
    execution: runtimePluginExecutionAvailabilitySchema,
    connectionHealth: z.array(pluginConnectionHealthSchema).optional(),
}).strict();
export const listRuntimePluginCatalogResponseSchema = z.object({
    plugins: z.array(runtimePluginCatalogEntrySchema),
});
